import { ComboboxInput } from '@headlessui/react';
import { cn } from 'cn';
import Icon from '../../Icon';
import type { Option } from '../InputCombobox';

export interface ComboboxMultiFieldProps<T> {
  disabled: boolean;
  errorId: string;
  hasError: boolean;
  inputId: string;
  name: string;
  onBackspace: React.KeyboardEventHandler<HTMLInputElement>;
  onQueryChange: (query: string) => void;
  onRemove: (value: T) => void;
  placeholder?: string;
  query: string;
  selectedOptions: Option<T>[];
}

export function InputComboboxMultiField<T>({
  disabled,
  errorId,
  hasError,
  inputId,
  name,
  onBackspace,
  onQueryChange,
  onRemove,
  placeholder,
  query,
  selectedOptions,
}: ComboboxMultiFieldProps<T>) {
  return (
    <div
      className={cn(
        'flex min-h-10 w-full flex-wrap items-center gap-2 pl-3 pr-10 py-1',
        'kunde:pl-4',
        'border border-neutral-10 bg-neutral-5 laane:rounded kunde:rounded-md',
        'typ-ui kunde:typ-body transition-colors',
        'kunde:border-neutral-40 kunde:text-sm',
        {
          'hover:border-neutral-40 hover:bg-neutral-10 focus-within:border-blue-20 focus-within:bg-white focus-within:ring-1 focus-within:ring-blue-20':
            !disabled,
          'focus-within:kunde:ring-2 focus-within:kunde:ring-blue-60':
            !disabled,
          'cursor-not-allowed border-neutral-40 bg-neutral-10 text-neutral-100':
            disabled,
          'kunde:text-neutral-60': disabled,
        },
      )}
    >
      {selectedOptions.map((option) => (
        <button
          key={String(option.value)}
          type="button"
          className={cn(
            'inline-flex h-5 items-center gap-2 rounded-lg bg-blue-100 px-2 text-white',
            'laane:typ-ui-sm kunde:typ-body-sm',
            { 'cursor-not-allowed bg-neutral-40 text-neutral-0': disabled },
          )}
          aria-label={`Remove ${option.label}`}
          onMouseDown={(event) => event.preventDefault()}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onRemove(option.value);
          }}
        >
          <span className="truncate">{option.label}</span>
          <Icon type="cross" size="tiny" className="size-3" />
        </button>
      ))}
      <ComboboxInput
        onChange={(event) => onQueryChange(event.target.value)}
        onKeyDown={onBackspace}
        value={query}
        className={cn(
          'flex-1 min-w-24 border-0! bg-transparent! p-0!',
          'focus:border-0! focus:ring-0!',
          'enabled:hover:border-0! enabled:hover:bg-transparent!',
          'disabled:bg-transparent!',
        )}
        aria-describedby={hasError ? errorId : undefined}
        aria-invalid={hasError}
        displayValue={() => ''}
        placeholder={selectedOptions.length > 0 ? '' : (placeholder ?? '')}
        name={name}
        id={inputId}
      />
    </div>
  );
}
